class CustomBigInt {
	digits: number[] | null;
	isNeg: boolean;

	constructor(flag?: boolean) {
		if (typeof flag === 'boolean' && flag === true) {
			this.digits = null;
		} else {
			this.digits = ZERO_ARRAY.slice(0);
		}
		this.isNeg = false;
	}
}

type MaybeBigInt = CustomBigInt | null;

const biRadixBits = 16;
const bitsPerDigit = biRadixBits;
const biRadix = 1 << 16;
const biHalfRadix = biRadix >>> 1;
const biRadixSquared = biRadix * biRadix;
const maxDigitVal = biRadix - 1;
let maxDigits = 20;
let ZERO_ARRAY: number[] = [];
let bigZero: MaybeBigInt, bigOne: MaybeBigInt;

function setMaxDigits(value: number) {
	maxDigits = value;
	ZERO_ARRAY = new Array(maxDigits).fill(0);
	bigZero = new CustomBigInt();
	bigOne = new CustomBigInt();
	if (bigOne.digits) {
		bigOne.digits[0] = 1;
	}
}

const dpl10 = 15;
const lr10 = biFromNumber(1000000000000000);



function biFromDecimal(s: string): CustomBigInt {
	const isNeg = s.charAt(0) === '-';
	let i = isNeg ? 1 : 0;
	let result: CustomBigInt;
	while (i < s.length && s.charAt(i) === '0') ++i;
	if (i === s.length) {
		result = new CustomBigInt();
	} else {
		const digitCount = s.length - i;
		let fgl = digitCount % dpl10;
		if (fgl === 0) fgl = dpl10;
		result = biFromNumber(Number(s.substr(i, fgl)));
		i += fgl;
		while (i < s.length) {
			result = biAdd(biMultiply(result, lr10), biFromNumber(Number(s.substr(i, dpl10))));
			i += dpl10;
		}
		result.isNeg = isNeg;
	}
	return result;
}

function biCopy(bi: CustomBigInt): CustomBigInt {
	const result = new CustomBigInt(true);
	result.digits = bi.digits ? bi.digits.slice(0) : null;
	result.isNeg = bi.isNeg;
	return result;
}

function biFromNumber(i: number): CustomBigInt {
	const result = new CustomBigInt();
	result.isNeg = i < 0;
	i = Math.abs(i);
	let j = 0;
	while (i > 0) {
		result.digits![j++] = i & maxDigitVal;
		i = Math.floor(i / biRadix);
	}
	return result;
}

function reverseStr(s: string): string {
	return s.split('').reverse().join('');
}

const hexatrigesimalToChar = [
	'0', '1', '2', '3', '4', '5', '6', '7', '8', '9',
	'a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j',
	'k', 'l', 'm', 'n', 'o', 'p', 'q', 'r', 's', 't',
	'u', 'v', 'w', 'x', 'y', 'z'
];

function biToString(x: CustomBigInt, radix: number): string {
	const b = new CustomBigInt();
	b.digits![0] = radix;
	let qr = biDivideModulo(x, b);
	let result = hexatrigesimalToChar[qr[1].digits![0]];
	while (biCompare(qr[0], bigZero!) === 1) {
		qr = biDivideModulo(qr[0], b);
		result += hexatrigesimalToChar[qr[1].digits![0]];
	}
	return (x.isNeg ? '-' : '') + reverseStr(result);
}

function biToDecimal(x: CustomBigInt): string {
	const b = new CustomBigInt();
	b.digits![0] = 10;
	let qr = biDivideModulo(x, b);
	let result = String(qr[1].digits![0]);
	while (biCompare(qr[0], bigZero!) === 1) {
		qr = biDivideModulo(qr[0], b);
		result += String(qr[1].digits![0]);
	}
	return (x.isNeg ? '-' : '') + reverseStr(result);
}

const hexToChar = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', 'a', 'b', 'c', 'd', 'e', 'f'];

function digitToHex(n: number): string {
	let result = '';
	for (let i = 0; i < 4; ++i) {
		result += hexToChar[n & 0xf];
		n >>>= 4;
	}
	return reverseStr(result);
}

function biToHex(x: CustomBigInt): string {
	let result = '';
	for (let i = biHighIndex(x); i > -1; --i) {
		result += digitToHex(x.digits![i]);
	}
	return result;
}

function charToHex(c: number): number {
	const ZERO = 48;
	const NINE = ZERO + 9;
	const littleA = 97;
	const littleZ = littleA + 25;
	const bigA = 65;
	const bigZ = bigA + 25;

	if (c >= ZERO && c <= NINE) return c - ZERO;
	if (c >= bigA && c <= bigZ) return 10 + c - bigA;
	if (c >= littleA && c <= littleZ) return 10 + c - littleA;
	return 0;
}

function hexToDigit(s: string): number {
	let result = 0;
	const sl = Math.min(s.length, 4);
	for (let i = 0; i < sl; ++i) {
		result <<= 4;
		result |= charToHex(s.charCodeAt(i));
	}
	return result;
}

function biFromHex(s: string): CustomBigInt {
	const result = new CustomBigInt();
	const sl = s.length;
	for (let i = sl, j = 0; i > 0; i -= 4, ++j) {
		result.digits![j] = hexToDigit(s.substr(Math.max(i - 4, 0), Math.min(i, 4)));
	}
	return result;
}

function biFromString(s: string, radix: number): CustomBigInt {
	const isNeg = s.charAt(0) === '-';
	const istop = isNeg ? 1 : 0;
	let result = new CustomBigInt();
	let place = new CustomBigInt();
	place.digits![0] = 1;
	for (let i = s.length - 1; i >= istop; i--) {
		const c = s.charCodeAt(i);
		const digit = charToHex(c);
		const biDigit = biMultiplyDigit(place, digit);
		result = biAdd(result, biDigit);
		place = biMultiplyDigit(place, radix);
	}
	result.isNeg = isNeg;
	return result;
}

function biAdd(x: CustomBigInt, y: CustomBigInt): CustomBigInt {
	let result;
	if (x.isNeg !== y.isNeg) {
		y.isNeg = !y.isNeg;
		result = biSubtract(x, y);
		y.isNeg = !y.isNeg;
	} else {
		result = new CustomBigInt();
		let c = 0;
		for (let i = 0; i < x.digits!.length; ++i) {
			const n = x.digits![i] + y.digits![i] + c;
			result.digits![i] = n % biRadix;
			c = Number(n >= biRadix);
		}
		result.isNeg = x.isNeg;
	}
	return result;
}

function biSubtract(x: CustomBigInt, y: CustomBigInt): CustomBigInt {
	let result: CustomBigInt;
	if (x.isNeg !== y.isNeg) {
		y.isNeg = !y.isNeg;
		result = biAdd(x, y);
		y.isNeg = !y.isNeg;
	} else {
		result = new CustomBigInt();
		let c = 0;
		for (let i = 0; i < x.digits!.length; ++i) {
			const n = x.digits![i] - y.digits![i] + c;
			result.digits![i] = n % biRadix;
			if (result.digits![i] < 0) result.digits![i] += biRadix;
			c = n < 0 ? -1 : 0;
		}
		if (c === -1) {
			c = 0;
			for (let i = 0; i < x.digits!.length; ++i) {
				const n = 0 - result.digits![i] + c;
				result.digits![i] = n % biRadix;
				if (result.digits![i] < 0) result.digits![i] += biRadix;
				c = n < 0 ? -1 : 0;
			}
			result.isNeg = !x.isNeg;
		} else {
			result.isNeg = x.isNeg;
		}
	}
	return result;
}

function biHighIndex(x: CustomBigInt): number {
	let result = x.digits!.length - 1;
	while (result > 0 && x.digits![result] === 0) --result;
	return result;
}

function biNumBits(x: CustomBigInt): number {
	const n = biHighIndex(x);
	let d = x.digits![n];
	const m = (n + 1) * bitsPerDigit;
	let result: number;
	for (result = m; result > m - bitsPerDigit; --result) {
		if ((d & 0x8000) !== 0) break;
		d <<= 1;
	}
	return result;
}

function biMultiply(x: CustomBigInt, y: CustomBigInt): CustomBigInt {
	const result = new CustomBigInt();
	const n = biHighIndex(x);
	const t = biHighIndex(y);
	let c: number;
	for (let i = 0; i <= t; ++i) {
		c = 0;
		let k = i;
		for (let j = 0; j <= n; ++j, ++k) {
			const uv = result.digits![k] + x.digits![j] * y.digits![i] + c;
			result.digits![k] = uv & maxDigitVal;
			c = uv >>> biRadixBits;
		}
		result.digits![i + n + 1] = c;
	}
	result.isNeg = x.isNeg !== y.isNeg;
	return result;
}

function biMultiplyDigit(x: CustomBigInt, y: number): CustomBigInt {
	const result = new CustomBigInt();
	let c = 0;
	const n = biHighIndex(x);
	for (let j = 0; j <= n; ++j) {
		const uv = result.digits![j] + x.digits![j] * y + c;
		result.digits![j] = uv & maxDigitVal;
		c = uv >>> biRadixBits;
	}
	result.digits![1 + n] = c;
	return result;
}

function biDivide(x: CustomBigInt, y: CustomBigInt): CustomBigInt {
	return biDivideModulo(x, y)[0];
}

function biModulo(x: CustomBigInt, y: CustomBigInt): CustomBigInt {
	return biDivideModulo(x, y)[1];
}

function biMultiplyMod(x: CustomBigInt, y: CustomBigInt, m: CustomBigInt): CustomBigInt {
	return biModulo(biMultiply(x, y), m);
}

function biPow(x: CustomBigInt, y: number): CustomBigInt {
	let result = bigOne!;
	let a = x;
	while (true) {
		if ((y & 1) !== 0) result = biMultiply(result, a);
		y >>= 1;
		if (y === 0) break;
		a = biMultiply(a, a);
	}
	return result;
}

function biPowMod(x: CustomBigInt, y: CustomBigInt, m: CustomBigInt): CustomBigInt {
	let result = bigOne!;
	let a = x;
	let k = y;
	while (true) {
		if ((k.digits![0] & 1) !== 0) result = biMultiplyMod(result, a, m);
		k = biShiftRight(k, 1);
		if (k.digits![0] === 0 && biHighIndex(k) === 0) break;
		a = biMultiplyMod(a, a, m);
	}
	return result;
}

// RSAFactory and RSACrypt class with TypeScript type annotations

class RSAFactory {
	modulus: CustomBigInt;
	k: number;
	mu: CustomBigInt;
	bkplus1: CustomBigInt;

	constructor(m: CustomBigInt) {
		this.modulus = biCopy(m);
		this.k = biHighIndex(this.modulus) + 1;
		const b2k = new CustomBigInt();
		b2k.digits![2 * this.k] = 1;
		this.mu = biDivide(b2k, this.modulus);
		this.bkplus1 = new CustomBigInt();
		this.bkplus1.digits![this.k + 1] = 1;
	}

	modulo(x: CustomBigInt): CustomBigInt {
		const q1 = biDivideByRadixPower(x, this.k - 1);
		const q2 = biMultiply(q1, this.mu);
		const q3 = biDivideByRadixPower(q2, this.k + 1);
		const r1 = biModuloByRadixPower(x, this.k + 1);
		const r2term = biMultiply(q3, this.modulus);
		const r2 = biModuloByRadixPower(r2term, this.k + 1);
		let r = biSubtract(r1, r2);
		if (r.isNeg) r = biAdd(r, this.bkplus1);
		while (biCompare(r, this.modulus) >= 0) r = biSubtract(r, this.modulus);
		return r;
	}

	multiplyMod(x: CustomBigInt, y: CustomBigInt): CustomBigInt {
		const xy = biMultiply(x, y);
		return this.modulo(xy);
	}

	powMod(x: CustomBigInt, y: CustomBigInt): CustomBigInt {
		let result = new CustomBigInt();
		result.digits![0] = 1;
		let a = x;
		let k = y;
		while (true) {
			if ((k.digits![0] & 1) !== 0) result = this.multiplyMod(result, a);
			k = biShiftRight(k, 1);
			if (k.digits![0] === 0 && biHighIndex(k) === 0) break;
			a = this.multiplyMod(a, a);
		}
		return result;
	}
}

class RSACrypt {
	e: CustomBigInt;
	d: CustomBigInt;
	m: CustomBigInt;
	digitSize: number;
	chunkSize: number;
	radix: number;
	factory: RSAFactory;

	constructor(exponent: string, modulus: string, keySize: number) {
		const maxDig = keySize ? keySize : 1024;
		setMaxDigits(maxDig / 8 + 1);
		this.e = biFromHex(exponent);
		this.d = biFromHex(exponent); // Change this if exponent is different for decryption
		this.m = biFromHex(modulus);

		this.digitSize = 2 * biHighIndex(this.m) + 2;
		this.chunkSize = this.digitSize - 11;

		this.radix = 16;
		this.factory = new RSAFactory(this.m);
	}

	encryptedString(s: string): string {
		if (this.chunkSize > this.digitSize - 11) {
			return 'Error';
		}

		const a: number[] = [];
		for (let i = 0; i < s.length; i++) {
			a[i] = s.charCodeAt(i);
		}

		let result = '';
		for (let i = 0; i < a.length; i += this.chunkSize) {
			const block = new CustomBigInt();
			const b: number[] = [];

			const msgLength = (i + this.chunkSize) > a.length ? a.length % this.chunkSize : this.chunkSize;
			for (let x = 0; x < msgLength; x++) {
				b[x] = a[i + msgLength - 1 - x];
			}
			b[msgLength] = 0;

			const paddedSize = Math.max(8, this.digitSize - 3 - msgLength);
			for (let x = 0; x < paddedSize; x++) {
				b[msgLength + 1 + x] = Math.floor(Math.random() * 254) + 1;
			}

			b[this.digitSize - 2] = 2;
			b[this.digitSize - 1] = 0;

			let j = 0;
			for (let k = 0; k < this.digitSize; ++j) {
				block.digits![j] = b[k++];
				block.digits![j] += b[k++] << 8;
			}

			const crypt = this.factory.powMod(block, this.e);
			const text = this.radix === 16 ? biToHex(crypt) : biToString(crypt, this.radix);
			result += text + ' ';
		}

		return result.trim();
	}

	decryptedString(s: string): string {
		const blocks = s.split(' ');
		let result = '';
		for (let i = 0; i < blocks.length; ++i) {
			let bi: CustomBigInt;
			if (this.radix === 16) {
				bi = biFromHex(blocks[i]);
			} else {
				bi = biFromString(blocks[i], this.radix);
			}

			const block = this.factory.powMod(bi, this.d);
			for (let j = 0; j <= biHighIndex(block); ++j) {
				result += String.fromCharCode(block.digits![j] & 255, block.digits![j] >> 8);
			}
		}

		if (result.charCodeAt(result.length - 1) === 0) {
			result = result.substring(0, result.length - 1);
		}
		return result.split('').reverse().join('');
	}
}

function biDivideModulo(x: CustomBigInt, y: CustomBigInt): [CustomBigInt, CustomBigInt] {
	const nb = biNumBits(x);
	let tb = biNumBits(y);
	const origYIsNeg = y.isNeg;
	let q: CustomBigInt, r: CustomBigInt;

	if (nb < tb) {
		if (x.isNeg) {
			q = biCopy(bigOne!);
			q.isNeg = !y.isNeg;
			x.isNeg = false;
			y.isNeg = false;
			r = biSubtract(y, x);
			x.isNeg = true;
			y.isNeg = origYIsNeg;
		} else {
			q = new CustomBigInt();
			r = biCopy(x);
		}
		return [q, r];
	}

	q = new CustomBigInt();
	r = biCopy(x);

	let t = Math.ceil(tb / bitsPerDigit) - 1;
	let lambda = 0;
	while (y.digits![t] < biHalfRadix) {
		y = biShiftLeft(y, 1);
		++lambda;
		++tb;
		t = Math.ceil(tb / bitsPerDigit) - 1;
	}
	r = biShiftLeft(r, lambda);
	const n = Math.ceil(nb / bitsPerDigit) - 1;

	const b = biMultiplyByRadixPower(y, n - t);
	while (biCompare(r, b) !== -1) {
		++q.digits![n - t];
		r = biSubtract(r, b);
	}

	for (let i = n; i > t; --i) {
		const ri = (i >= r.digits!.length) ? 0 : r.digits![i];
		const ri1 = (i - 1 >= r.digits!.length) ? 0 : r.digits![i - 1];
		const ri2 = (i - 2 >= r.digits!.length) ? 0 : r.digits![i - 2];
		const yt = (t >= y.digits!.length) ? 0 : y.digits![t];
		const yt1 = (t - 1 >= y.digits!.length) ? 0 : y.digits![t - 1];

		if (ri === yt) {
			q.digits![i - t - 1] = maxDigitVal;
		} else {
			q.digits![i - t - 1] = Math.floor((ri * biRadix + ri1) / yt);
		}

		let c1 = q.digits![i - t - 1] * (yt * biRadix + yt1);
		let c2 = ri * biRadixSquared + (ri1 * biRadix + ri2);
		while (c1 > c2) {
			--q.digits![i - t - 1];
			c1 = q.digits![i - t - 1] * (yt * biRadix + yt1);
			c2 = ri * biRadixSquared + (ri1 * biRadix + ri2);
		}

		const bTemp = biMultiplyByRadixPower(y, i - t - 1);
		r = biSubtract(r, biMultiplyDigit(bTemp, q.digits![i - t - 1]));
		if (r.isNeg) {
			r = biAdd(r, bTemp);
			--q.digits![i - t - 1];
		}
	}

	r = biShiftRight(r, lambda);
	q.isNeg = x.isNeg !== origYIsNeg;
	if (x.isNeg) {
		if (origYIsNeg) {
			q = biAdd(q, bigOne!);
		} else {
			q = biSubtract(q, bigOne!);
		}
		r = biSubtract(y, r);
	}
	if (r.digits![0] === 0 && biHighIndex(r) === 0) r.isNeg = false;

	return [q, r];
}

function biShiftLeft(x: CustomBigInt, n: number): CustomBigInt {
	const digitCount = Math.floor(n / bitsPerDigit);
	const result = new CustomBigInt();
	arrayCopy(x.digits!, 0, result.digits!, digitCount, result.digits!.length - digitCount);
	const bits = n % bitsPerDigit;
	const rightBits = bitsPerDigit - bits;

	for (let i = result.digits!.length - 1, i1 = i - 1; i > 0; --i, --i1) {
		result.digits![i] = ((result.digits![i] << bits) & maxDigitVal) |
			((result.digits![i1] & highBitMasks[bits]) >>> rightBits);
	}
	result.digits![0] = ((result.digits![0] << bits) & maxDigitVal);
	result.isNeg = x.isNeg;
	return result;
}

function biShiftRight(x: CustomBigInt, n: number): CustomBigInt {
	const digitCount = Math.floor(n / bitsPerDigit);
	const result = new CustomBigInt();
	arrayCopy(x.digits!, digitCount, result.digits!, 0, x.digits!.length - digitCount);
	const bits = n % bitsPerDigit;
	const leftBits = bitsPerDigit - bits;

	for (let i = 0, i1 = i + 1; i < result.digits!.length - 1; ++i, ++i1) {
		result.digits![i] = (result.digits![i] >>> bits) |
			((result.digits![i1] & lowBitMasks[bits]) << leftBits);
	}
	result.digits![result.digits!.length - 1] >>>= bits;
	result.isNeg = x.isNeg;
	return result;
}

function biMultiplyByRadixPower(x: CustomBigInt, n: number): CustomBigInt {
	const result = new CustomBigInt();
	arrayCopy(x.digits!, 0, result.digits!, n, result.digits!.length - n);
	return result;
}

function biDivideByRadixPower(x: CustomBigInt, n: number): CustomBigInt {
	const result = new CustomBigInt();
	arrayCopy(x.digits!, n, result.digits!, 0, result.digits!.length - n);
	return result;
}
CustomBigInt
function biModuloByRadixPower(x: CustomBigInt, n: number): CustomBigInt {
	const result = new CustomBigInt();
	arrayCopy(x.digits!, 0, result.digits!, 0, n);
	return result;
}

function biCompare(x: CustomBigInt, y: CustomBigInt): number {
	if (x.isNeg !== y.isNeg) {
		return 1 - 2 * Number(x.isNeg);
	}
	for (let i = x.digits!.length - 1; i >= 0; --i) {
		if (x.digits![i] !== y.digits![i]) {
			if (x.isNeg) {
				return 1 - 2 * Number(x.digits![i] > y.digits![i]);
			} else {
				return 1 - 2 * Number(x.digits![i] < y.digits![i]);
			}
		}
	}
	return 0;
}

// Helper function to copy arrays
function arrayCopy(src: number[], srcStart: number, dest: number[], destStart: number, n: number) {
	const m = Math.min(srcStart + n, src.length);
	for (let i = srcStart, j = destStart; i < m; ++i, ++j) {
		dest[j] = src[i];
	}
}

const highBitMasks = [
	0x0000, 0x8000, 0xC000, 0xE000, 0xF000, 0xF800,
	0xFC00, 0xFE00, 0xFF00, 0xFF80, 0xFFC0, 0xFFE0,
	0xFFF0, 0xFFF8, 0xFFFC, 0xFFFE, 0xFFFF
];

const lowBitMasks = [
	0x0000, 0x0001, 0x0003, 0x0007, 0x000F, 0x001F,
	0x003F, 0x007F, 0x00FF, 0x01FF, 0x03FF, 0x07FF,
	0x0FFF, 0x1FFF, 0x3FFF, 0x7FFF, 0xFFFF
];


export { RSACrypt };
